import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { alertsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Bell, AlertTriangle, Clock, CreditCard, Check, CheckCheck, User as UserIcon, Trophy, MailX, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const alertTypeConfig = {
  low_entries: { icon: AlertTriangle, color: 'yellow', label: 'Ședințe Puține' },
  expiring_soon: { icon: Clock, color: 'orange', label: 'Expiră Curând' },
  expired: { icon: CreditCard, color: 'red', label: 'Expirat' },
  no_plan: { icon: CreditCard, color: 'gray', label: 'Fără Abonament' },
  milestone: { icon: Trophy, color: 'gold', label: 'Milestone' },
  email_bounce: { icon: MailX, color: 'red', label: 'Email Eșuat' },
};

const ConfettiOverlay = () => {
  const colors = ['#6db025', '#00C2FF', '#FFD700', '#FF6B6B', '#C084FC', '#34D399'];
  const pieces = Array.from({ length: 50 }, (_, i) => ({
    id: i, color: colors[i % colors.length], left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 3}s`, duration: `${2 + Math.random() * 3}s`, size: 4 + Math.random() * 8,
  }));
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <style>{`@keyframes confetti-fall { 0% { transform: translateY(-20px) rotate(0deg); opacity: 1; } 100% { transform: translateY(100vh) rotate(720deg); opacity: 0; } }`}</style>
      {pieces.map(p => <div key={p.id} style={{ position:'absolute', left:p.left, top:'-10px', width:`${p.size}px`, height:`${p.size*0.6}px`, backgroundColor:p.color, animation:`confetti-fall ${p.duration} ${p.delay} ease-out forwards` }} />)}
    </div>
  );
};

const AlertsPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unseen');
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiShownRef = useRef(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = filter === 'unseen' ? { unseen_only: true } : {};
      const response = await alertsAPI.getAll(params);
      setAlerts(response.data);
      if (!confettiShownRef.current) {
        const now = new Date();
        const hasMilestone = (response.data || []).some(a => a.alert_type === 'milestone' && (now - new Date(a.created_at)) < 24*60*60*1000);
        if (hasMilestone) { setShowConfetti(true); confettiShownRef.current = true; setTimeout(() => setShowConfetti(false), 6000); }
      }
    } catch { toast.error('Eroare la încărcarea alertelor'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleMarkSeen = async (alert, e) => {
    e.preventDefault(); e.stopPropagation();
    const alertId = alert.id || alert._id;
    if (!alertId) { toast.error('Eroare internă'); return; }
    try { await alertsAPI.markSeen(alertId); toast.success('Rezolvat!'); fetchAlerts(); } catch { toast.error('Eroare'); }
  };
  const handleMarkAllSeen = async () => {
    try { await alertsAPI.markAllSeen(); toast.success('Toate rezolvate!'); fetchAlerts(); } catch { toast.error('Eroare'); }
  };
  const handleDeleteAlert = async (alert, e) => {
    e.preventDefault(); e.stopPropagation();
    const alertId = alert.id || alert._id;
    if (!alertId) return;
    try { await alertsAPI.delete(alertId); fetchAlerts(); } catch { toast.error('Eroare la ștergere'); }
  };
  const handleDeleteAllSeen = async () => {
    try { await alertsAPI.deleteAllSeen(); toast.success('Istoricul a fost curățat!'); fetchAlerts(); } catch { toast.error('Eroare'); }
  };

  const unseenCount = alerts.filter(a => !a.is_seen).length;
  const now = new Date();
  const recentMilestones = alerts.filter(a => a.alert_type === 'milestone' && (now - new Date(a.created_at)) < 24*60*60*1000);
  const regularAlerts = alerts.filter(a => !(a.alert_type === 'milestone' && (now - new Date(a.created_at)) < 24*60*60*1000));

  return (
    <div className="space-y-6">
      {showConfetti && <ConfettiOverlay />}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Alerte Sistem</h1>
          <p className="text-white/40 text-sm mt-1">{unseenCount > 0 ? <span className="text-yellow-500 font-bold">{unseenCount} notificări</span> : 'Zero notificări.'}</p>
        </div>
        <div className="flex gap-2">
          {unseenCount > 0 && <button onClick={handleMarkAllSeen} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-white/50 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors rounded"><CheckCheck size={14} /> Marchează tot</button>}
          {filter === 'all' && <button onClick={handleDeleteAllSeen} className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider hover:bg-red-500/20 transition-colors rounded"><Trash2 size={14} /> Șterge istoricul</button>}
        </div>
      </div>

      {recentMilestones.length > 0 && (
        <div className="relative overflow-hidden border border-yellow-400/30 bg-gradient-to-r from-yellow-500/10 via-amber-500/5 to-yellow-500/10 p-6 rounded">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center"><Trophy size={24} className="text-yellow-400" /></div>
            <div><h2 className="font-heading text-xl font-bold text-yellow-400 uppercase">Realizări Recente!</h2><p className="text-yellow-400/50 text-[10px] uppercase font-bold tracking-widest">Ultimele 24h</p></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {recentMilestones.map(a => (
              <div key={a.id} className="flex items-center gap-4 p-4 bg-black/30 border border-yellow-400/10 rounded-sm">
                <div className="text-center"><p className="font-heading text-3xl font-black text-yellow-400">{a.milestone_value || '?'}</p><p className="text-yellow-400/40 text-[8px] uppercase font-bold">Prezențe</p></div>
                <div className="flex-1 min-w-0"><p className="text-white font-bold text-sm uppercase truncate">{a.person_name}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={() => setFilter('unseen')} className={`px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${filter === 'unseen' ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>Doar Noi</button>
        <button onClick={() => setFilter('all')} className={`px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${filter === 'all' ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>Istoric Complet</button>
      </div>

      {loading ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-16 text-center text-[#6db025] animate-pulse font-heading text-xs tracking-widest uppercase rounded">Se încarcă...</div>
      ) : regularAlerts.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center flex flex-col items-center gap-4 opacity-30 rounded"><Bell size={48} className="text-white" /><p className="text-white font-bold uppercase tracking-widest text-sm">{filter === 'unseen' ? 'Nicio alertă nouă' : 'Inbox gol'}</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {regularAlerts.map(alert => {
            const config = alertTypeConfig[alert.alert_type] || alertTypeConfig.no_plan;
            const Icon = config.icon;
            const isMilestone = alert.alert_type === 'milestone';
            const stripeClass = isMilestone ? 'bg-yellow-400' : !alert.is_seen ? 'bg-[#6db025]' : 'bg-white/10';
            return (
              <div key={alert.id || alert._id} className={`relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden ${alert.is_seen && !isMilestone ? 'opacity-70' : ''}`}>
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${stripeClass}`} />
                <div className="p-4 pl-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 border ${config.color === 'gold' ? 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400' : config.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500' : config.color === 'orange' ? 'bg-orange-500/10 border-orange-500/20 text-orange-500' : config.color === 'red' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-white/5 border-white/10 text-white/40'}`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className={`text-[10px] uppercase font-black tracking-widest px-2 py-0.5 rounded ${config.color === 'gold' ? 'bg-yellow-400/20 text-yellow-400' : config.color === 'yellow' ? 'bg-yellow-500/20 text-yellow-500' : config.color === 'orange' ? 'bg-orange-500/20 text-orange-500' : config.color === 'red' ? 'bg-red-500/20 text-red-500' : 'bg-white/10 text-white/40'}`}>{config.label}</span>
                        {!alert.is_seen && !isMilestone && <span className="text-[10px] text-[#6db025] font-black uppercase tracking-widest">Nerezolvată</span>}
                      </div>
                      <p className={`text-sm leading-relaxed ${!alert.is_seen ? 'text-white font-bold' : 'text-white/60'}`}>{alert.message}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] uppercase font-bold tracking-widest flex-wrap">
                        <span className="text-white/30">{format(new Date(alert.created_at), 'dd MMM yyyy • HH:mm', { locale: ro })}</span>
                        {alert.user_id && <Link to={`/admin/utilizatori/${alert.user_id}`} className="text-[#6db025] hover:underline flex items-center gap-1.5 relative z-10"><UserIcon size={12} /> Vezi Cont</Link>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1 pt-2 border-t border-white/[0.04]">
                    {!alert.is_seen && (
                      <button onClick={(e) => handleMarkSeen(alert, e)} className="flex items-center gap-1.5 px-3 py-2 bg-[#6db025]/10 border border-[#6db025]/20 text-[#6db025] hover:bg-[#6db025] hover:text-black transition-colors rounded text-[11px] font-black uppercase tracking-widest">
                        <Check size={15} /> Rezolvat
                      </button>
                    )}
                    <button onClick={(e) => handleDeleteAlert(alert, e)} className="p-2 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded" title="Șterge alertă"><Trash2 size={18} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
